import { AttachMoneyOutlined, FindInPage } from "@mui/icons-material";
import { Box, Card, Divider, Stack, Typography } from "@mui/material";
import React from "react";
import BellCurve from "../../../components/graphs/BellCurve";
import ExplanationDialog from "../../../components/misc/ExplanationDialog";
import { useCase } from "../../../contexts/CaseContext";
import Translations from "../../../lang/en/pages.json";
import { formatNumberWithSuffix, formatNumberWithSuffixPrefix } from "../../../utils";
import CenteredBox from "./CenteredBox";
import Likelihood from "./Likelihood";

const SettlementDamages: React.FC<{
  total: { start: number; end: number };
  chartData: any;
}> = ({ total, chartData }) => {
  return (
    <Card
      id="damages"
      sx={{
        height: "100%",
        width: "100%",
        backgroundColor: "white",
        alignItems: "center",
        direction: "row",
      }}
    >
      <Stack height="100%" alignItems={"center"} direction={"row"} spacing={2}>
        <Box width={"40%"}>
          <CenteredBox
            icon={<AttachMoneyOutlined fontSize="large" />}
            title={
              <Stack direction={"row"} spacing={1}>
                <Typography variant={"body2"}>
                  {
                    Translations.pages.dashboard_page.tabs.settlement
                      .damages_block.title.normal
                  }
                </Typography>
                <Typography fontWeight={"bold"} variant={"body2"}>
                  {
                    Translations.pages.dashboard_page.tabs.settlement
                      .damages_block.title.bold
                  }
                </Typography>
              </Stack>
            }
            range={{
              start: formatNumberWithSuffixPrefix(total.start, "$"),
              end: formatNumberWithSuffixPrefix(total.end, "$"),
            }}
          />
        </Box>
        <Divider orientation="vertical" />
        <Stack spacing={2} alignItems={"center"}>
          <Box textAlign={"right"} width={"100%"}>
            <Typography variant="caption" sx={{ color: "rgba(0, 0, 0, 0.56)" }}>
              {
                Translations.pages.dashboard_page.tabs.settlement.damages_block
                  .description
              }
            </Typography>
          </Box>
          <BellCurve width={600} height={230} data={chartData} />
        </Stack>
      </Stack>
    </Card>
  );
};

const EmptySettlementCard: React.FC = () => {
  return (
    <Card
      sx={{
        height: "100%",
        width: "100%",
        backgroundColor: "white",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <FindInPage
        sx={{
          fontSize: 60,
          color: "#757575",
          mb: 2,
        }}
      />
      <Typography
        variant="h5"
        sx={{
          color: "#757575",
          mb: 1,
          fontWeight: "bold",
        }}
      >
        No Data Available
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ textAlign: "center" }}
      >
        Settlement analysis requires historical case records to activate.
      </Typography>
    </Card>
  );
};

const Settlement: React.FC = () => {
  const { currentOutcome } = useCase() || {};
  const [isExplanationOpen, setIsExplanationOpen] = React.useState(false);

  const settlementDamages = currentOutcome?.predictions?.settlement_damages;
  const settlementLikelihood =
    currentOutcome?.predictions?.resolution_likelihood?.settlement;

  if (!settlementLikelihood || !settlementDamages) {
    return <EmptySettlementCard />;
  }

  const LikelihoodCard: React.FC = () => {
    const { currentOutcome } = useCase() || {};
    const settlementLikelihood =
      currentOutcome?.predictions?.resolution_likelihood?.settlement;

    if (!settlementLikelihood) {
      return <EmptySettlementCard />;
    }

    return (
      <Card
        id="likelihood"
        sx={{ display: "flex", backgroundColor: "white", width: "30%" }}
      >
        <Likelihood
          type="settlement"
          start={settlementLikelihood.confidence_interval?.min ?? 0}
          end={settlementLikelihood.confidence_interval?.max ?? 0}
          showInfoIcon={Boolean(settlementLikelihood?.explanation)}
          onInfoClick={() => setIsExplanationOpen(true)}
          infoIconProps={{
            backgroundColor: "#5E35B1",
            fontSize: 24,
          }}
        />

        <ExplanationDialog
          title={
            Translations.pages.dashboard_page.tabs.settlement
              .settlement_likelihood_block.dialog.title
          }
          open={isExplanationOpen}
          onClose={() => setIsExplanationOpen(false)}
          explanation={settlementLikelihood.explanation}
        />
      </Card>
    );
  };

  <Card
    id="likelihood"
    sx={{ display: "flex", backgroundColor: "white", width: "30%" }}
  >
    <Likelihood
      type="settlement"
      start={settlementLikelihood.confidence_interval?.min ?? 0}
      end={settlementLikelihood.confidence_interval?.max ?? 0}
      showInfoIcon={Boolean(settlementLikelihood?.explanation)}
      onInfoClick={() => setIsExplanationOpen(true)}
      infoIconProps={{
        backgroundColor: "#5E35B1",
        fontSize: 24,
      }}
    />

    <ExplanationDialog
      title={
        Translations.pages.dashboard_page.tabs.settlement
          .settlement_likelihood_block.dialog.title
      }
      open={isExplanationOpen}
      onClose={() => setIsExplanationOpen(false)}
      explanation={settlementLikelihood.explanation}
    />
  </Card>;

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ width: "100%", height: "100%" }}
      alignItems="stretch"
    >
      <LikelihoodCard />
      <SettlementDamages
        total={{
          start: settlementDamages.min ?? 0,
          end: settlementDamages.max ?? 0,
        }}
        chartData={
          settlementDamages.data_points?.map(([x, y]) => ({ x, y })) ?? []
        }
      />
    </Stack>
  );
};

export default Settlement;
